import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PageShell from "../common/PageShell";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

// ---------------------------------------------------------------------------
// Status display config
// ---------------------------------------------------------------------------

const DISPLAY = {
  approved: {
    title: "Identity Verified",
    body: "Your identity has been successfully verified. A Verified badge will appear on your profile.",
    severity: "success",
    icon: <VerifiedUserIcon sx={{ fontSize: 48, color: "success.main" }} />,
  },
  in_review: {
    title: "Verification Under Review",
    body: "Your verification was submitted and is being reviewed. The Verified badge is added only after approval. You will be notified once a decision is made.",
    severity: "info",
    icon: <HourglassTopIcon sx={{ fontSize: 48, color: "info.main" }} />,
  },
  in_progress: {
    title: "Verification In Progress",
    body: "Your verification session has been submitted. A decision will be made shortly.",
    severity: "info",
    icon: <HourglassTopIcon sx={{ fontSize: 48, color: "info.main" }} />,
  },
  not_started: {
    title: "Verification Submitted",
    body: "Your verification has been submitted and is being processed. Check back here for an update.",
    severity: "info",
    icon: <HourglassTopIcon sx={{ fontSize: 48, color: "info.main" }} />,
  },
  declined: {
    title: "Verification Declined",
    body: "Unfortunately your verification was declined. You can return to your account to try again.",
    severity: "error",
    icon: <ErrorOutlineIcon sx={{ fontSize: 48, color: "error.main" }} />,
  },
  abandoned: {
    title: "Verification Not Completed",
    body: "It looks like you did not finish the verification process. You can return to your account and try again.",
    severity: "warning",
    icon: null,
  },
  expired: {
    title: "Verification Session Expired",
    body: "Your verification session has expired. Return to your account to start a new one.",
    severity: "warning",
    icon: null,
  },
};

const PROCESSING_DISPLAY = {
  title: "Processing…",
  body: "Please wait while we confirm your verification status.",
  severity: "info",
  icon: null,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function KycCallbackPage() {
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);

  // Didit appends these to the callback URL as hints — we read them for UX
  // but we never trust them as final state.
  const hintStatus = queryParams.get("status") || null;
  const hintSessionId = queryParams.get("verificationSessionId") || null;

  const [phase, setPhase] = useState("loading"); // 'loading' | 'done'
  const [kycStatus, setKycStatus] = useState(null);
  const [error, setError] = useState(null);

  // Scrub Didit params from browser history as soon as possible.
  useEffect(() => {
    if (hintStatus || hintSessionId) {
      window.history.replaceState({}, "", location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify the backend that the user returned, and fetch the current status.
  useEffect(() => {
    let cancelled = false;

    async function notifyAndFetch() {
      try {
        const res = await fetch(`${basePath}/api/v1/account/kyc/callback-seen`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => null);
        if (!cancelled) {
          if (res.ok && data?.ok) {
            setKycStatus(data.kyc?.status || null);
          } else {
            // Fall back to read-only status endpoint.
            await fetchFallbackStatus();
          }
        }
      } catch (_) {
        if (!cancelled) {
          await fetchFallbackStatus();
        }
      } finally {
        if (!cancelled) setPhase("done");
      }
    }

    async function fetchFallbackStatus() {
      try {
        const res = await fetch(`${basePath}/api/v1/account/kyc/status`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => null);
        if (res.ok && data?.ok) {
          setKycStatus(data.kyc?.status || null);
        } else {
          setError("Could not confirm your verification status. Your account page will have the latest status.");
        }
      } catch (_) {
        setError("Could not confirm your verification status. Your account page will have the latest status.");
      }
    }

    notifyAndFetch();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // While loading, provide a preliminary hint from the query params if available.
  const displayStatus = phase === "loading"
    ? (hintStatus ? mapHintStatus(hintStatus) : null)
    : kycStatus;

  const display = phase === "loading"
    ? (displayStatus ? DISPLAY[displayStatus] : PROCESSING_DISPLAY)
    : (DISPLAY[displayStatus] || PROCESSING_DISPLAY);

  return (
    <PageShell>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          textAlign: "center",
          p: 4,
          maxWidth: 560,
          mx: "auto",
        }}
      >
        {phase === "loading" && !displayStatus ? (
          <CircularProgress sx={{ mb: 3 }} />
        ) : display.icon ? (
          <Box sx={{ mb: 2 }}>{display.icon}</Box>
        ) : null}

        <Typography variant="h5" fontWeight={700} gutterBottom>
          {display.title}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {display.body}
        </Typography>

        {error && (
          <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
            <AlertTitle>Status check unavailable</AlertTitle>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={() => history.push("/account")}
            sx={{ textTransform: "none" }}
          >
            Back to my account
          </Button>
        </Box>
      </Box>
    </PageShell>
  );
}

// Map the Didit-provided hint status values to our internal ones.
function mapHintStatus(hintStatus) {
  const map = {
    Approved: "approved",
    Declined: "declined",
    "In Review": "in_review",
    "In Progress": "in_progress",
    approved: "approved",
    declined: "declined",
    in_review: "in_review",
    in_progress: "in_progress",
  };
  return map[hintStatus] || null;
}
